import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateWorkShift } from '@/hooks/useWorkShifts';
import { ShiftType } from '@/types/workshift';

interface ShiftFormData {
  date: string;
  shiftType: ShiftType;
  startTime: string;
  endTime: string;
  breakDuration: number;
  notes: string;
}

interface AddDailyWorkShiftProps {
  selectedYear: number;
  selectedMonth: number;
}

interface ShiftTypeConfig {
  value: ShiftType;
  labelKey: string;
  defaultStart: string;
  defaultEnd: string;
  color: string;
}

const shiftTypes: ShiftTypeConfig[] = [
  { value: 'full-day', labelKey: 'fullDay', defaultStart: '09:00', defaultEnd: '18:00', color: 'blue' },
  { value: 'morning', labelKey: 'morning', defaultStart: '06:00', defaultEnd: '12:00', color: 'amber' },
  { value: 'afternoon', labelKey: 'afternoon', defaultStart: '12:00', defaultEnd: '18:00', color: 'orange' },
  { value: 'night', labelKey: 'night', defaultStart: '18:00', defaultEnd: '02:00', color: 'indigo' },
  { value: 'half-day', labelKey: 'halfDay', defaultStart: '09:00', defaultEnd: '13:00', color: 'cyan' },
];

// Helper to get the default date based on selected year/month
const getDefaultDate = (year: number, month: number): string => {
  const today = new Date();
  // If selected month is current month, use today's date
  if (year === today.getFullYear() && month === today.getMonth() + 1) {
    return today.toISOString().split('T')[0];
  }
  // Otherwise use the first day of the selected month
  return `${year}-${String(month).padStart(2, '0')}-01`;
};

const AddDailyWorkShift: React.FC<AddDailyWorkShiftProps> = ({
  selectedYear,
  selectedMonth,
}) => {
  const { t } = useTranslation('work');
  const defaultDate = getDefaultDate(selectedYear, selectedMonth);

  const [formData, setFormData] = useState<ShiftFormData>({
    date: defaultDate,
    shiftType: 'full-day',
    startTime: '09:00',
    endTime: '18:00',
    breakDuration: 60,
    notes: '',
  });

  // Update form date when selected year/month changes
  useEffect(() => {
    const newDefaultDate = getDefaultDate(selectedYear, selectedMonth);
    setFormData(prev => ({ ...prev, date: newDefaultDate }));
  }, [selectedYear, selectedMonth]);

  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const createWorkShift = useCreateWorkShift();

  const handleShiftTypeChange = (type: ShiftType) => {
    const shiftConfig = shiftTypes.find(s => s.value === type);
    if (shiftConfig) {
      setFormData({
        ...formData,
        shiftType: type,
        startTime: shiftConfig.defaultStart,
        endTime: shiftConfig.defaultEnd,
      });
    }
  };

  const calculateWorkHours = (): number => {
    const [startHour, startMin] = formData.startTime.split(':').map(Number);
    let [endHour, endMin] = formData.endTime.split(':').map(Number);

    // Handle overnight shifts
    if (endHour < startHour) {
      endHour += 24;
    }

    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    const workMinutes = endMinutes - startMinutes - formData.breakDuration;

    return Math.max(0, workMinutes / 60);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    try {
      await createWorkShift.mutateAsync({
        date: formData.date,
        shift_type: formData.shiftType,
        start_time: formData.startTime,
        end_time: formData.endTime,
        break_duration: formData.breakDuration,
        notes: formData.notes || undefined,
      });

      setSubmitSuccess(true);

      // Reset form after success
      setTimeout(() => {
        setSubmitSuccess(false);
        setFormData({
          date: getDefaultDate(selectedYear, selectedMonth),
          shiftType: 'full-day',
          startTime: '09:00',
          endTime: '18:00',
          breakDuration: 60,
          notes: '',
        });
      }, 2000);
    } catch (error: any) {
      setSubmitError(error.message || 'Failed to add work shift');
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('addShift.title')}</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">{t('addShift.subtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Date Selection */}
        <div>
          <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('addShift.date')}
          </label>
          <input
            type="date"
            id="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            required
          />
        </div>

        {/* Shift Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {t('addShift.shiftType')}
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {shiftTypes.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => handleShiftTypeChange(type.value)}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  formData.shiftType === type.value
                    ? `border-${type.color}-500 bg-${type.color}-50 ring-2 ring-${type.color}-200`
                    : 'border-gray-200 hover:border-gray-300 dark:border-gray-600'
                }`}
              >
                <div className={`font-medium ${
                  formData.shiftType === type.value ? `text-${type.color}-700` : 'text-gray-900 dark:text-white'
                }`}>
                  {t(`addShift.shiftTypes.${type.labelKey}`)}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {type.defaultStart} - {type.defaultEnd}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Time Selection */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="startTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('addShift.startTime')}
            </label>
            <input
              type="time"
              id="startTime"
              value={formData.startTime}
              onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              required
            />
          </div>
          <div>
            <label htmlFor="endTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('addShift.endTime')}
            </label>
            <input
              type="time"
              id="endTime"
              value={formData.endTime}
              onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              required
            />
          </div>
        </div>

        {/* Break Duration */}
        <div>
          <label htmlFor="breakDuration" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('addShift.breakDuration')}
          </label>
          <select
            id="breakDuration"
            value={formData.breakDuration}
            onChange={(e) => setFormData({ ...formData, breakDuration: Number(e.target.value) })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          >
            <option value={0}>{t('addShift.breakOptions.noBreak')}</option>
            <option value={15}>{t('addShift.breakOptions.15min')}</option>
            <option value={30}>{t('addShift.breakOptions.30min')}</option>
            <option value={45}>{t('addShift.breakOptions.45min')}</option>
            <option value={60}>{t('addShift.breakOptions.1hour')}</option>
            <option value={90}>{t('addShift.breakOptions.1.5hours')}</option>
            <option value={120}>{t('addShift.breakOptions.2hours')}</option>
          </select>
        </div>

        {/* Work Hours Summary */}
        <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('addShift.calculatedHours')}</span>
            <span className="text-lg font-bold text-purple-600 dark:text-purple-400">
              {calculateWorkHours().toFixed(1)} {t('addShift.hours')}
            </span>
          </div>
        </div>

        {/* Notes */}
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('addShift.notes')}
          </label>
          <textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            placeholder={t('addShift.notesPlaceholder')}
          />
        </div>

        {/* Error Message */}
        {submitError && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">{submitError}</p>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex items-center justify-end space-x-4">
          {submitSuccess && (
            <div className="flex items-center text-green-600">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm font-medium">{t('addShift.successMessage')}</span>
            </div>
          )}
          <button
            type="submit"
            disabled={createWorkShift.isLoading}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createWorkShift.isLoading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {t('addShift.submitting')}
              </span>
            ) : (
              t('addShift.submitButton')
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddDailyWorkShift;
